import { z } from 'zod';
import { FlowRoleSchema } from '../flow/role';
import { NoteRecordKindSchema, PostNoteBodySchema } from '../flow/notes';
import { RECONCILE_REASONS } from '../schemas/gong-reconcile-report';

/**
 * Canonical catalog for Flow analytics events (Segment → Snowflake).
 *
 * Source of truth per CLAUDE.md "Schemas Are the Source of Truth": event names
 * and property shapes live here as Zod schemas; TS types are `z.infer`. Both
 * the server emitter (src/services/segment.ts `trackServerEvent`) and the
 * client wrapper (web/app-home useSegmentTrack / usePageTracking, via the
 * `@shared/analytics/events` alias) import from this one module so the two
 * never drift. Replaces the old ad-hoc TS union in useTrackViewViewed.ts and
 * the bare `interface` for the MCP event in segment.ts.
 *
 * Full spec + metric mapping: Notion "Flow Analytics — Event Tracking Spec".
 */

/** Segment event names (Title Case with spaces, per Segment convention). */
export const ANALYTICS_EVENTS = {
  PAGE_VIEWED: 'Page Viewed',
  PILL_CLICKED: 'Pill Clicked',
  CONTACT_ADDED_TO_QUEUE: 'Contact Added to Queue',
  CONTACT_REMOVED_FROM_QUEUE: 'Contact Removed from Queue',
  PLAY_ASSIGNED: 'Play Assigned',
  CONTACT_WORKED: 'Contact Worked',
  CONTACT_SEND_FAILED: 'Contact Send Failed',
  CONTACT_ENRICHED: 'Contact Enriched',
  /** Server-side, once per handled Trellus dialer hint (not memo replays): what the hint resolved to and why. */
  DIALER_HINT_RESOLVED: 'Dialer Hint Resolved',
  /** Action View → Slack Notifications v0 (plan
   * `2026-08-12-002-action-view-slack-notifications-plan.md`, U3, decision
   * 10). Emitted server-side: SENT by the DBOS cron (U2) on a successful
   * `chat.postMessage`; CLICKED by the `/open/:actionId` redirect handler
   * (separate unit) when `?source=slack_notification` is present. */
  SLACK_NOTIFICATION_SENT: 'Slack Notification Sent',
  SLACK_NOTIFICATION_CLICKED: 'Slack Notification Clicked',
  /** Account/Contact Notes (plan `2026-10-21-001-account-contact-notes-chat-plan.md`, U10b).
   * NOTE_POSTED is server-side (first post-save step); NOTES_OPENED is client-side
   * (`useOpenNotes`); NOTE_MENTION_SLACK_SENT is server-side, once per note that pinged >= 1
   * teammate. Deliberately not `SLACK_NOTIFICATION_SENT` (that needs Action View rule ids). */
  NOTE_POSTED: 'Note Posted',
  NOTES_OPENED: 'Notes Opened',
  NOTE_MENTION_SLACK_SENT: 'Note Mention Slack Sent',
  /** Restored Action View (Signals tab) client events — names are the OLD
   * Title Case strings from the deleted `mini-apps/action-view` and are never
   * renamed (warehouse continuity). See plan
   * `2026-10-23-001-restore-action-view-analytics-plan.md`. */
  ACTION_MARKED_COMPLETED: 'Action Marked Completed',
  ACTION_ARCHIVED: 'Action Archived',
  ACTION_EXPANDED: 'Action Expanded',
  ACTION_VIEW_LOADED: 'Action View Loaded',
  ACTION_VIEW_TAB_CHANGED: 'Action View Tab Changed',
  ACTION_OPENED_IN_GONG: 'Action Opened in Gong',
  /** Server-side: every instant-action execute (fire time + terminal outcome). */
  ACTION_EXECUTED: 'Action Executed',
} as const;

export type AnalyticsEventName = (typeof ANALYTICS_EVENTS)[keyof typeof ANALYTICS_EVENTS];

// ---------------------------------------------------------------------------
// Shared enums / primitives
// ---------------------------------------------------------------------------

/** Where in the app an event happened. `Page Viewed` is app-wide; the rest
 * are Flow-loop surfaces. `other` catches unmapped routes for page tracking. */
export const SurfaceSchema = z.enum([
  'command_center',
  'playhouse',
  'action_view',
  'flow_shell',
  'queue',
  'library',
  'categories',
  'my_actions',
  'pipeline',
  'admin',
  'signals',
  'other',
]);
export type Surface = z.infer<typeof SurfaceSchema>;

export const EnvSchema = z.enum(['production', 'staging', 'development']);
export type AnalyticsEnv = z.infer<typeof EnvSchema>;

/** SFDC ids are strings (003…/001…); DB-native ids may arrive numeric.
 * Kept permissive so a shape mismatch never drops a telemetry event. */
const IdSchema = z.union([z.string(), z.number()]).nullable().optional();

/**
 * Play reference, mirroring the `enrollment_items` DB columns we actually have
 * at server emit sites: `play_type_id` (numeric FK) + `play_instance_ref`. The
 * human-readable `play_type` slug (event_invite / generate_message / …) is left
 * optional — it's cheap to resolve as a Snowflake dimension off `play_type_id`
 * and isn't always in scope at emit time, so we never fabricate it here.
 */
const playRefShape = {
  play_type_id: z.number().nullable().optional(),
  play_instance_ref: z.string().nullable().optional(),
  play_type: z.string().nullable().optional(),
} as const;

// ---------------------------------------------------------------------------
// Identity spine — auto-stamped on every event by the wrapper / server emitter
// ---------------------------------------------------------------------------

/**
 * Rep identity carried on every event. `rep_id` (numeric Terra user id) is the
 * canonical key and is present on the queue path. Mini-app SFDC routes only
 * carry `rep_slack_id` (action-view) or `rep_email` (playhouse); those are
 * emitted alongside so nothing is lost and the warehouse can reconcile against
 * the `identify` / USERS table. Identity fields are permissive on purpose —
 * validation strictness lives on the event-specific discriminators below.
 */
export const IdentityPropsSchema = z.object({
  rep_id: z.number().nullable().optional(),
  rep_slack_id: z.string().nullable().optional(),
  rep_email: z.string().nullable().optional(),
  user_type: z.string().nullable().optional(),
  flow_role: FlowRoleSchema.nullable().optional(),
  impersonator_id: z.union([z.number(), z.string()]).nullable().optional(),
  env: EnvSchema,
  /** The client SPA route + surface the rep was on when the event fired,
   * stamped from the `x-flow-path` / `x-flow-surface` request headers (see
   * src/http/request-origin.ts). Present on the request-driven paths; null on
   * background/cron emits and mini-app SFDC routes that have no originating
   * page. Lets funnels attribute a server action to the page that drove it
   * without a warehouse join against the `Page Viewed` stream. */
  origin_path: z.string().nullable().optional(),
  origin_surface: SurfaceSchema.nullable().optional(),
});
export type IdentityProps = z.infer<typeof IdentityPropsSchema>;

// ---------------------------------------------------------------------------
// Per-event property schemas
// ---------------------------------------------------------------------------

export const PageViewedPropsSchema = IdentityPropsSchema.extend({
  page_name: z.string(),
  path: z.string(),
  surface: SurfaceSchema,
  is_flow_surface: z.boolean(),
});
export type PageViewedProps = z.infer<typeof PageViewedPropsSchema>;

export const PillClickedPropsSchema = IdentityPropsSchema.extend({
  /** The contact-state fact dimension (e.g. queued, recent_request, open_task,
   * invited). Owned by the contact-state module — kept a permissive string here
   * so the analytics catalog never drifts from / duplicates that enum. */
  dimension: z.string(),
  link_kind: z.enum(['url', 'intent']),
  evidence_ref: z.string().nullable().optional(),
  contact_id: IdSchema,
  account_id: IdSchema,
  surface: SurfaceSchema,
});
export type PillClickedProps = z.infer<typeof PillClickedPropsSchema>;

/** `surface` is nullable on the queue-lifecycle events: the server doesn't
 * know which app the rep queued from (the request doesn't carry it), and the
 * repo forbids guessed defaults. A client-supplied surface can populate it
 * later; null means "genuinely unknown", not a fabricated value. */
export const ContactAddedToQueuePropsSchema = IdentityPropsSchema.extend({
  ...playRefShape,
  contact_id: IdSchema,
  account_id: IdSchema,
  source_context: z.string().nullable().optional(),
  surface: SurfaceSchema.nullable().optional(),
  /** Bulk adds only: the number of contacts in the request. */
  batch_size: z.number().int().nullable().optional(),
});
export type ContactAddedToQueueProps = z.infer<typeof ContactAddedToQueuePropsSchema>;

export const ContactRemovedFromQueuePropsSchema = IdentityPropsSchema.extend({
  ...playRefShape,
  contact_id: IdSchema,
  account_id: IdSchema,
  reason: z.string().nullable().optional(),
  time_in_queue_ms: z.number().nullable().optional(),
  surface: SurfaceSchema.nullable().optional(),
});
export type ContactRemovedFromQueueProps = z.infer<typeof ContactRemovedFromQueuePropsSchema>;

export const PlayAssignedPropsSchema = IdentityPropsSchema.extend({
  ...playRefShape,
  contact_id: IdSchema,
  account_id: IdSchema,
  previous_play_type_id: z.number().nullable().optional(),
  surface: SurfaceSchema.nullable().optional(),
});
export type PlayAssignedProps = z.infer<typeof PlayAssignedPropsSchema>;

export const ContactWorkedPropsSchema = IdentityPropsSchema.extend({
  ...playRefShape,
  action_type: z.enum([
    'play_sent',
    'invite_sent',
    'task_completed',
    'task_archived',
    'task_opened_in_gong',
    /** The rep removed the contact from the Gong flow in the Trellus dialer (U18, `source: 'trellus'`). */
    'flow_removed',
  ]),
  surface: SurfaceSchema,
  outcome: z.enum(['sent', 'completed', 'archived', 'opened']),
  contact_id: IdSchema,
  account_id: IdSchema,
  /** @deprecated history-only (Playhouse invites are campaign-scoped; nothing
   * emits this any more, but existing warehouse rows carry it). */
  campaign_id: IdSchema,
  /** terra = closed from the Terra UI (vs gong/sfdc passthrough); trellus = a dialer hint re-checked through reconcile (U18). */
  source: z.enum(['terra', 'gong', 'sfdc', 'trellus']).nullable().optional(),
  correlation_id: z.string().nullable().optional(),
  execution_id: z.string().nullable().optional(),
  signal_action_id: z.string().nullable().optional(),
  /** Step signature of the Signal Action worked (snooze / Gong open). */
  step_type: z.string().nullable().optional(),
  step_number: z.number().int().nullable().optional(),
  /** `task_completed` via Mark as read: `indefinite` = "Indefinitely", `dated` = a dated snooze. */
  snooze_kind: z.enum(['indefinite', 'dated']).nullable().optional(),
  /** ISO instant the snooze ends; null for indefinite. */
  snooze_until: z.string().nullable().optional(),
  /** How the rep reached the action (currently only the Slack verb button). */
  entry_point: z.enum(['slack_notification']).nullable().optional(),
});
export type ContactWorkedProps = z.infer<typeof ContactWorkedPropsSchema>;

/**
 * Why a dialer hint ended the way it did: every reconcile reason (one tuple, in
 * `gong-reconcile-report.ts`) plus the dialer's own.
 */
export const DIALER_HINT_REASONS = [
  ...RECONCILE_REASONS,
  /** The completion named a step the mirror had already moved past. */
  'stale_attribution',
  /** Refused by the durable hint interval / event-id claim. */
  'throttled',
  /** Gong's step count was unknown, so a last-step completion was treated as mid-step (snoozed). */
  'unknown_step_count',
  /** The completion carried no usable step, so only facts were re-checked. */
  'no_hint',
] as const;
export type DialerHintReason = (typeof DIALER_HINT_REASONS)[number];

export const DialerHintResolvedPropsSchema = IdentityPropsSchema.extend({
  kind: z.enum(['task_completed', 'flow_removed']),
  outcome: z.enum(['closed', 'snoozed', 'already_closed', 'already_snoozed', 'unchanged']),
  reason: z.enum(DIALER_HINT_REASONS).nullable(),
  /** `task_completed` only: how the event's step compared with the mirror's. */
  attribution: z.enum(['match', 'absent', 'no_mirror_step', 'stale', 'ahead']).nullable(),
  dialer_mode: z.enum(['report', 'apply']),
  signal_action_id: z.string(),
  contact_id: IdSchema,
  /** The mirror's step for the row. */
  step_number: z.number().int().nullable(),
  /** The step the Trellus event named. */
  body_step_number: z.number().int().nullable(),
});
export type DialerHintResolvedProps = z.infer<typeof DialerHintResolvedPropsSchema>;

export const ContactSendFailedPropsSchema = IdentityPropsSchema.extend({
  ...playRefShape,
  contact_id: IdSchema,
  account_id: IdSchema,
  campaign_id: IdSchema,
  surface: SurfaceSchema,
  reason: z.enum(['dedup', 'already_invited', 'no_email', 'error']),
  error_message: z.string().nullable().optional(),
});
export type ContactSendFailedProps = z.infer<typeof ContactSendFailedPropsSchema>;

/** The known enrichment kinds. Emitted alongside `action_slug` (ground truth);
 * nullable because enrichment_type is a mapped label off the ActionDefinition
 * slug — an unmapped slug is honestly `null` (never a guessed default). */
export const EnrichmentTypeSchema = z.enum(['wrong_phone', 'find_phone', 'enrich_contact', 'enrich_company']);
export type EnrichmentType = z.infer<typeof EnrichmentTypeSchema>;

/** Explicit ActionDefinition-slug → enrichment_type map. Extend as new
 * enrichment actions are added (via the /create-contact-enrichment skill). */
const ENRICHMENT_TYPE_BY_SLUG: Record<string, EnrichmentType> = {
  reenrich_phone: 'wrong_phone',
  find_phone_by_name: 'find_phone',
};

/** Map an enrichment action's slug to its type, or null if not yet mapped. */
export function enrichmentTypeForSlug(slug: string): EnrichmentType | null {
  return ENRICHMENT_TYPE_BY_SLUG[slug] ?? null;
}

export const ContactEnrichedPropsSchema = IdentityPropsSchema.extend({
  /** ActionDefinition slug — ground-truth identifier for the enrichment run. */
  action_slug: z.string(),
  enrichment_type: EnrichmentTypeSchema.nullable().optional(),
  /** `started` = fired (the Q3 "enrichments run" count); terminal outcomes
   * (`success`/`no_result`/`failed`) are emitted at the completion chokepoint
   * (restore-action-view-analytics plan, U7). */
  outcome: z.enum(['started', 'success', 'no_result', 'failed']).nullable().optional(),
  surface: SurfaceSchema.nullable().optional(),
  contact_id: IdSchema,
  account_id: IdSchema,
  found_value_present: z.boolean().nullable().optional(),
  execution_id: z.string().nullable().optional(),
});
export type ContactEnrichedProps = z.infer<typeof ContactEnrichedPropsSchema>;

/** Every instant-action execute: `started` at fire time, `delivered`/`failed`
 * at the completion chokepoint. `is_enrichment` marks overlay-writing actions. */
export const ActionExecutedPropsSchema = IdentityPropsSchema.extend({
  action_slug: z.string(),
  execution_id: z.string().nullable().optional(),
  outcome: z.enum(['started', 'delivered', 'failed']),
  is_enrichment: z.boolean(),
  surface: SurfaceSchema.nullable().optional(),
  contact_id: IdSchema,
  account_id: IdSchema,
  source: z.string().nullable().optional(),
});
export type ActionExecutedProps = z.infer<typeof ActionExecutedPropsSchema>;

// Restored Action View client events (Signals tab). camelCase props are the
// old payloads preserved verbatim; `source` is the new feature marker.
const signalsClientShape = {
  source: z.literal('signals'),
} as const;

export const ActionMarkedCompletedPropsSchema = IdentityPropsSchema.extend({
  ...signalsClientShape,
  signalActionId: z.string(),
  signalType: z.string().nullable().optional(),
  stepType: z.string().nullable().optional(),
  stepNumber: z.number().int().nullable().optional(),
  snoozeKind: z.enum(['indefinite', 'dated']).nullable().optional(),
});
export type ActionMarkedCompletedProps = z.infer<typeof ActionMarkedCompletedPropsSchema>;

export const ActionArchivedPropsSchema = IdentityPropsSchema.extend({
  ...signalsClientShape,
  signalActionId: z.string(),
  reason: z.string().nullable().optional(),
  signalType: z.string().nullable().optional(),
});
export type ActionArchivedProps = z.infer<typeof ActionArchivedPropsSchema>;

export const ActionExpandedPropsSchema = IdentityPropsSchema.extend({
  ...signalsClientShape,
  signalActionId: z.string(),
  signalType: z.string().nullable().optional(),
  assigneeEmail: z.string().nullable().optional(),
});
export type ActionExpandedProps = z.infer<typeof ActionExpandedPropsSchema>;

export const ActionViewLoadedPropsSchema = IdentityPropsSchema.extend({
  ...signalsClientShape,
  tab: z.string(),
  actionCount: z.number().int(),
});
export type ActionViewLoadedProps = z.infer<typeof ActionViewLoadedPropsSchema>;

export const ActionViewTabChangedPropsSchema = IdentityPropsSchema.extend({
  ...signalsClientShape,
  tab: z.string(),
});
export type ActionViewTabChangedProps = z.infer<typeof ActionViewTabChangedPropsSchema>;

/** `signalActionId` is nullable: best-effort client event, non-signal_action rows have none. */
export const ActionOpenedInGongPropsSchema = IdentityPropsSchema.extend({
  ...signalsClientShape,
  signalActionId: z.string().nullable().optional(),
  contactId: IdSchema,
  accountId: IdSchema,
  stepType: z.string().nullable().optional(),
});
export type ActionOpenedInGongProps = z.infer<typeof ActionOpenedInGongPropsSchema>;

/**
 * Shared discriminators for both Slack-notification events — the SA/rule/
 * step context a "why did Terra ping me?" view would need (plan U3).
 * `signal_action_id`/`rule_id` are required (unlike the optional
 * `signal_action_id` on `ContactWorkedPropsSchema`): every emit site for
 * these two events has both by construction (the claim-table PK).
 */
const slackNotificationBaseShape = {
  signal_action_id: z.string(),
  rule_id: z.string(),
  /** The rule's full identity key — `{type, subtype, playType}`, the same
   * triple the Priority Ranker groups Signal Actions by. `action_type` alone
   * is NOT a discriminator: two real ranker rows share the type "GTM Play"
   * and differ only in subtype, so a warehouse query keyed on the type would
   * silently merge them. Both extra fields are required and spell "none" as
   * `''`, mirroring the `NOT NULL DEFAULT ''` rule columns they are read off
   * (`action_view_notification_rule`) — every emit site has a matched rule
   * row by construction, so an absent key means a broken emit, not an
   * unknowable value. `rule_id` still identifies the row; these exist so the
   * two GTM Play rows are distinguishable WITHOUT that join. */
  action_type: z.string(),
  action_subtype: z.string(),
  play_type: z.string(),
  step_type: z.string().nullable().optional(),
} as const;

export const SlackNotificationSentPropsSchema = IdentityPropsSchema.extend(
  slackNotificationBaseShape,
);
export type SlackNotificationSentProps = z.infer<typeof SlackNotificationSentPropsSchema>;

export const SlackNotificationClickedPropsSchema = IdentityPropsSchema.extend({
  ...slackNotificationBaseShape,
  /** `open_in_terra` added 2026-08-13: `usePageTracking`'s `Page Viewed`
   * event only sends `location.pathname`, never the query string, so the
   * `?source=slack_notification` marker on that URL was silently invisible
   * to analytics — NOT "for free" as originally assumed. `ActionQueuePage`
   * now fires this event explicitly (via a small server round-trip so the
   * rule_id/action_type context above is available the same way the verb
   * button gets it) when it mounts on a deep link carrying that marker. */
  button: z.enum(['verb', 'open_in_terra']),
});
export type SlackNotificationClickedProps = z.infer<typeof SlackNotificationClickedPropsSchema>;

export const NotePostedPropsSchema = IdentityPropsSchema.extend({
  record_kind: NoteRecordKindSchema,
  mention_count: z.number(),
  /** Same enum the PATCH body carries (`PostNoteBodySchema.source`). */
  source: PostNoteBodySchema.shape.source.removeDefault(),
});
export type NotePostedProps = z.infer<typeof NotePostedPropsSchema>;

export const NotesOpenedEntryPointSchema = z.enum(['rail_tab', 'row_bubble', 'header_button', 'deep_link', 'chip', 'save_as_note']);
export type NotesOpenedEntryPoint = z.infer<typeof NotesOpenedEntryPointSchema>;

export const NotesOpenedPropsSchema = IdentityPropsSchema.extend({
  record_kind: NoteRecordKindSchema,
  entry_point: NotesOpenedEntryPointSchema,
});
export type NotesOpenedProps = z.infer<typeof NotesOpenedPropsSchema>;

export const NoteMentionSlackSentPropsSchema = IdentityPropsSchema.extend({
  record_kind: NoteRecordKindSchema,
  mention_count: z.number(),
  recipient_count: z.number(),
});
export type NoteMentionSlackSentProps = z.infer<typeof NoteMentionSlackSentPropsSchema>;

// ---------------------------------------------------------------------------
// Registry: event name -> property schema
// ---------------------------------------------------------------------------

/**
 * Maps each event name to its property schema. Used by `trackServerEvent` to
 * validate (best-effort — a mismatch logs a warning but still emits; telemetry
 * is a side-channel, not an inbound trust boundary). The real guarantee is the
 * compile-time `z.infer` types on emit call sites.
 */
export const ANALYTICS_EVENT_SCHEMAS = {
  [ANALYTICS_EVENTS.PAGE_VIEWED]: PageViewedPropsSchema,
  [ANALYTICS_EVENTS.PILL_CLICKED]: PillClickedPropsSchema,
  [ANALYTICS_EVENTS.CONTACT_ADDED_TO_QUEUE]: ContactAddedToQueuePropsSchema,
  [ANALYTICS_EVENTS.CONTACT_REMOVED_FROM_QUEUE]: ContactRemovedFromQueuePropsSchema,
  [ANALYTICS_EVENTS.PLAY_ASSIGNED]: PlayAssignedPropsSchema,
  [ANALYTICS_EVENTS.CONTACT_WORKED]: ContactWorkedPropsSchema,
  [ANALYTICS_EVENTS.CONTACT_SEND_FAILED]: ContactSendFailedPropsSchema,
  [ANALYTICS_EVENTS.CONTACT_ENRICHED]: ContactEnrichedPropsSchema,
  [ANALYTICS_EVENTS.DIALER_HINT_RESOLVED]: DialerHintResolvedPropsSchema,
  [ANALYTICS_EVENTS.SLACK_NOTIFICATION_SENT]: SlackNotificationSentPropsSchema,
  [ANALYTICS_EVENTS.SLACK_NOTIFICATION_CLICKED]: SlackNotificationClickedPropsSchema,
  [ANALYTICS_EVENTS.NOTE_POSTED]: NotePostedPropsSchema,
  [ANALYTICS_EVENTS.NOTES_OPENED]: NotesOpenedPropsSchema,
  [ANALYTICS_EVENTS.NOTE_MENTION_SLACK_SENT]: NoteMentionSlackSentPropsSchema,
  [ANALYTICS_EVENTS.ACTION_MARKED_COMPLETED]: ActionMarkedCompletedPropsSchema,
  [ANALYTICS_EVENTS.ACTION_ARCHIVED]: ActionArchivedPropsSchema,
  [ANALYTICS_EVENTS.ACTION_EXPANDED]: ActionExpandedPropsSchema,
  [ANALYTICS_EVENTS.ACTION_VIEW_LOADED]: ActionViewLoadedPropsSchema,
  [ANALYTICS_EVENTS.ACTION_VIEW_TAB_CHANGED]: ActionViewTabChangedPropsSchema,
  [ANALYTICS_EVENTS.ACTION_OPENED_IN_GONG]: ActionOpenedInGongPropsSchema,
  [ANALYTICS_EVENTS.ACTION_EXECUTED]: ActionExecutedPropsSchema,
} as const satisfies Record<AnalyticsEventName, z.ZodType>;
